import {
  DocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from 'firebase/firestore';
import {
  DeckAttributeGroup,
  deckAttributeGroupConverter,
} from './deck-attribute-group.model';
import { UserGroup } from './user-group.model';
import { User, userConverter } from './user.model';

export const validateTimestamp = (
  timestamp: Timestamp | { nanoseconds: number; seconds: number } | undefined
) => {
  if (!timestamp) {
    return null;
  }
  return new Timestamp(timestamp.seconds, timestamp.nanoseconds);
};

export enum EducationalPath {
  HighSchool = 'high_school',
  SecondarySchool = 'secondary_school',
  GeneralSchool = 'general_school',
  PrimarySchool = 'primary_school',
}

export type DeckId = string;

export class Deck {
  id: DeckId;
  name?: string;
  author?: User; // save in document
  authorDisplayNameOverridden?: boolean;
  description?: string;
  active?: boolean;
  hidden?: boolean;
  numberOfCards?: number;
  numberOfDueCardsInPrepareMode?: number;
  numberOfDueCardsInStudyMode?: number;
  lastPrepareModeDueCardsLeft?: number;
  lastStudyModeDueCardsLeft?: number;
  lastVerifyModeDueCardsLeft?: number;
  sourceGroup?: UserGroup;
  studiedInPrepareMode: boolean = false;
  numberOfDueCardsUpdatedTimestamp?: Timestamp; // check whether number of due cards is up to date by comparing this timestamp with "today"
  lastStudiedAt?: Timestamp;
  updatedAt?: Timestamp;
  createdAt?: Timestamp;
  attributeGroups?: DeckAttributeGroup[];
  public: boolean = false;
  backgroundColor?: string;
  backgroundImageId?: string;
  backgroundImageUrl?: string;
  subject?: string;
  grade?: number;
  educationalPath?: string;
  // subcollections:
  // flashcards, prepareReviews, studyReviews

  constructor(
    id: string,
    name: string | undefined,
    author: User | undefined,
    description: string | undefined,
    active: boolean = true,
    hidden: boolean = false,
    numberOfCards?: number,
    numberOfDueCardsInPrepareMode?: number,
    numberOfDueCardsInStudyMode?: number,
    lastPrepareModeDueCardsLeft?: number,
    lastStudyModeDueCardsLeft?: number,
    lastVerifyModeDueCardsLeft?: number,
    sourceGroup?: UserGroup,
    studiedInPrepareMode: boolean = false,
    numberOfDueCardsUpdatedTimestamp?: Timestamp,
    updatedAt?: Timestamp,
    createdAt?: Timestamp,
    attributeGroups?: DeckAttributeGroup[],
    isPublic: boolean = true,
    backgroundColor?: string,
    backgroundImageId?: string,
    backgroundImageUrl?: string,
    subject?: string,
    grade?: number,
    educationalPath?: string,
    authorDisplayNameOverridden?: boolean,
    lastStudiedAt?: Timestamp
  ) {
    this.id = id;
    this.name = name;
    this.author = author;
    this.authorDisplayNameOverridden = authorDisplayNameOverridden ?? false;
    this.description = description;
    this.active = active;
    this.hidden = hidden;
    this.numberOfCards = numberOfCards;
    this.numberOfDueCardsInPrepareMode = numberOfDueCardsInPrepareMode;
    this.numberOfDueCardsInStudyMode = numberOfDueCardsInStudyMode;
    this.lastPrepareModeDueCardsLeft = lastPrepareModeDueCardsLeft ?? 0;
    this.lastStudyModeDueCardsLeft = lastStudyModeDueCardsLeft ?? 0;
    this.lastVerifyModeDueCardsLeft = lastVerifyModeDueCardsLeft ?? 0;
    this.sourceGroup = sourceGroup;
    this.studiedInPrepareMode = studiedInPrepareMode;
    this.numberOfDueCardsUpdatedTimestamp = numberOfDueCardsUpdatedTimestamp;
    this.lastStudiedAt = lastStudiedAt;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
    this.attributeGroups = attributeGroups;
    this.public = isPublic;
    this.backgroundColor = backgroundColor;
    this.backgroundImageId = backgroundImageId;
    this.backgroundImageUrl = backgroundImageUrl;
    this.subject = subject;
    this.grade = grade;
    this.educationalPath = educationalPath;
  }

  getProgress(): number {
    let numberOfCards = this.numberOfCards;
    if (numberOfCards === undefined || numberOfCards === 0) {
      numberOfCards = 1;
    }
    let numberOfDueCards = this.numberOfDueCardsInStudyMode;
    if (numberOfDueCards === undefined) {
      numberOfDueCards = 0;
    }
    return ((numberOfCards - numberOfDueCards) / numberOfCards) * 100;
  }
}

export const deckConverter = {
  toFirestore: (deck: Deck) => {
    return {
      name: deck.name,
      author: userConverter.toFirestore(
        deck.author
          ? deck.author
          : new User(
              'unknown-user',
              undefined,
              'student',
              undefined,
              undefined,
              undefined,
              false,
              0
            )
      ),
      description: deck.description || null,
      active: deck.active === undefined ? true : deck.active,
      hidden: deck.hidden === undefined ? false : deck.hidden,
      numberOfCards: deck.numberOfCards || 0,
      numberOfDueCardsInPrepareMode: deck.numberOfDueCardsInPrepareMode || 0,
      numberOfDueCardsInStudyMode: deck.numberOfDueCardsInStudyMode || 0,
      lastPrepareModeDueCardsLeft: deck.lastPrepareModeDueCardsLeft ?? 0,
      lastStudyModeDueCardsLeft: deck.lastStudyModeDueCardsLeft ?? 0,
      lastVerifyModeDueCardsLeft: deck.lastVerifyModeDueCardsLeft ?? 0,
      studiedInPrepareMode:
        deck.studiedInPrepareMode === undefined
          ? false
          : deck.studiedInPrepareMode,
      sourceGroup: deck.sourceGroup || null,
      numberOfDueCardsUpdatedTimestamp:
        validateTimestamp(deck.numberOfDueCardsUpdatedTimestamp) || null,
      updatedAt: validateTimestamp(Timestamp.now()) || null,
      createdAt: validateTimestamp(deck.createdAt) || null,
      attributeGroups:
        deck.attributeGroups?.map((group) => {
          return deckAttributeGroupConverter.toFirestore(group);
        }) || null,
      public: deck.public === undefined ? true : deck.public,
      backgroundColor: deck.backgroundColor || null,
      backgroundImageId: deck.backgroundImageId || null,
      backgroundImageUrl: deck.backgroundImageUrl || null,
      subject: deck.subject || null,
      grade: deck.grade || null,
      educationalPath: deck.educationalPath || null,
      authorDisplayNameOverridden: deck.authorDisplayNameOverridden || false,
      lastStudiedAt: validateTimestamp(deck.lastStudiedAt) || null,
    };
  },
  fromFirestore: (snapshot: DocumentSnapshot, options?: SnapshotOptions) => {
    if (!snapshot.exists()) {
      return undefined;
    }
    const data = snapshot.data(options);
    return new Deck(
      snapshot.id,
      data['name'] ?? undefined,
      userConverter.fromFirestore2(data['author']),
      data['description'] ?? undefined,
      data['active'],
      data['hidden'],
      data['numberOfCards'] ?? undefined,
      data['numberOfDueCardsInPrepareMode'] ?? undefined,
      data['numberOfDueCardsInStudyMode'] ?? undefined,
      data['lastPrepareModeDueCardsLeft'] ?? 0,
      data['lastStudyModeDueCardsLeft'] ?? 0,
      data['lastVerifyModeDueCardsLeft'] ?? 0,
      data['sourceGroup'] ?? undefined,
      data['studiedInPrepareMode'] ?? false,
      data['numberOfDueCardsUpdatedTimestamp'] ?? undefined,
      data['updatedAt'] ?? undefined,
      data['createdAt'] ?? undefined,
      (data['attributeGroups'] || []).map((group: unknown) => {
        return deckAttributeGroupConverter.fromObject(group);
      }),
      data['public'] ?? false,
      data['backgroundColor'] ?? undefined,
      data['backgroundImageId'] ?? undefined,
      data['backgroundImageUrl'] ?? undefined,
      data['subject'] ?? undefined,
      data['grade'] ?? undefined,
      data['educationalPath'] ?? undefined,
      data['authorDisplayNameOverridden'] ?? false,
      validateTimestamp(data['lastStudiedAt'] ?? undefined) ?? undefined
    );
  },
};
